import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, DownloadSimple, X, Star, Check, Image as ImageIcon, Trash } from '@phosphor-icons/react';
import { apiOrigin } from '@/utils/apiBase';

const API = apiOrigin();

export default function MyPhotosPage() {
  const { slug, token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lightbox, setLightbox] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/events/public/${slug}/my-photos/${token}`);
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Personal link is invalid.');
    }
    setLoading(false);
  }, [slug, token]);

  useEffect(() => { load(); }, [load]);

  const handleRemoveFromPhoto = async (photo) => {
    if (!window.confirm("Remove yourself from this photo?")) return;
    try {
      await axios.post(`${API}/api/events/public/${slug}/my-photos/${token}/unclaim/${photo.photo_id}`);
      setLightbox(null);
      await load();
    } catch { /* noop */ }
  };

  const downloadPhoto = async (photo) => {
    try {
      const res = await fetch(`${API}${photo.url}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${data.event.title.replace(/[^a-z0-9]/gi, '_')}_${photo.photo_id}.jpg`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch { /* noop */ }
  };

  const downloadAll = async () => {
    for (const p of data.photos) {
      // eslint-disable-next-line no-await-in-loop
      await downloadPhoto(p);
      // eslint-disable-next-line no-await-in-loop
      await new Promise(r => setTimeout(r, 250));
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A]">
      <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6 bg-[#0A0A0A] text-center">
      <div className="text-5xl mb-4">🔒</div>
      <h1 className="text-xl font-bold text-white mb-2">Invalid link</h1>
      <p className="text-sm text-neutral-400 max-w-sm">{error}</p>
      <Link to={`/party/${slug}`} className="mt-6 text-xs font-bold text-orange-400 inline-flex items-center gap-1.5">
        <ArrowLeft size={14} weight="bold" /> Back to RSVP
      </Link>
    </div>
  );

  const ev = data.event;
  const themeColor = ev.theme_color || '#FF6B35';
  const photos = data.photos || [];

  return (
    <div className="min-h-screen text-white" data-no-theme-shim style={{ background: '#0A0A0A' }} data-testid="my-photos-page">
      <div className="sticky top-0 z-30 px-4 sm:px-6 py-3 backdrop-blur-xl border-b"
        style={{ background: 'rgba(10,10,10,0.85)', borderColor: 'rgba(255,255,255,0.08)' }}>
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <Link to={`/party/${slug}/gallery`} className="inline-flex items-center gap-2 text-xs font-semibold text-neutral-200 hover:text-white">
            <ArrowLeft size={14} weight="bold" /> Full gallery
          </Link>
          <div className="text-center">
            <div className="text-[10px] uppercase tracking-wider font-bold" style={{ color: themeColor }}>
              {data.guest_name === 'you' ? 'My Highlights' : `${data.guest_name}'s Highlights`}
            </div>
            <div className="text-sm font-bold truncate max-w-[55vw] text-white">{ev.title}</div>
          </div>
          {photos.length > 0 ? (
            <button onClick={downloadAll} data-testid="download-all-btn"
              className="px-3 py-1.5 rounded-full text-[10px] font-bold inline-flex items-center gap-1.5"
              style={{ background: themeColor, color: '#fff' }}>
              <DownloadSimple size={12} weight="bold" /> All
            </button>
          ) : <div className="w-12" />}
        </div>
      </div>

      <div className="px-4 sm:px-6 pt-8 pb-6 text-center max-w-2xl mx-auto">
        <div className="text-4xl mb-3">⭐</div>
        <h1 className="text-2xl sm:text-3xl font-bold mb-2 text-white">
          {photos.length === 0 ? 'No photos found yet' : `${photos.length} photo${photos.length === 1 ? '' : 's'} found`}
        </h1>
        <p className="text-sm text-neutral-300">
          {photos.length === 0
            ? data.has_face_reference
              ? "We haven't matched you in any photos yet. Check back after the event."
              : 'Upload a clear selfie so we can find you in the party photos.'
            : 'Tap any photo to see the full size, download or remove yourself.'}
        </p>

        {!data.has_face_reference && (
          <FaceRefUploader slug={slug} token={token} themeColor={themeColor} onDone={load} />
        )}

        <div className="mt-3">
          <Link to={`/party/${slug}/gallery`} data-testid="link-to-full-gallery"
            className="inline-flex items-center gap-1.5 text-xs font-bold" style={{ color: themeColor }}>
            View the full party gallery →
          </Link>
        </div>
      </div>

      {photos.length > 0 && (
        <div className="px-3 sm:px-6 pb-24 max-w-6xl mx-auto">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 sm:gap-3">
            {photos.map(p => (
              <button key={p.photo_id} onClick={() => setLightbox(p)}
                data-testid={`my-photo-${p.photo_id}`}
                className="relative aspect-square rounded-xl overflow-hidden bg-neutral-900 group transition-transform hover:scale-[1.02]">
                <img src={`${API}${p.thumb_url}`} loading="lazy" alt="" className="w-full h-full object-cover" />
                {p.face_count > 1 && (
                  <div className="absolute top-2 left-2 px-1.5 py-0.5 rounded-md text-[9px] font-bold backdrop-blur-md"
                    style={{ background: 'rgba(0,0,0,0.6)' }}>
                    Group ({p.face_count})
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Lightbox */}
      {lightbox && (
        <div className="fixed inset-0 z-50 bg-black/95 flex flex-col" onClick={() => setLightbox(null)}>
          <div className="flex items-center justify-between p-4 text-white" onClick={e => e.stopPropagation()}>
            <button onClick={() => setLightbox(null)} className="p-2 rounded-full bg-white/10 hover:bg-white/20">
              <X size={18} weight="bold" />
            </button>
            <div className="flex gap-2">
              <button onClick={() => handleRemoveFromPhoto(lightbox)} data-testid="remove-from-photo"
                className="px-3 py-1.5 rounded-full bg-white/10 hover:bg-red-500/30 text-xs font-bold inline-flex items-center gap-1.5">
                <Trash size={14} weight="bold" /> Not me
              </button>
              <button onClick={() => downloadPhoto(lightbox)} data-testid="my-download-btn"
                className="px-3 py-1.5 rounded-full text-xs font-bold inline-flex items-center gap-1.5"
                style={{ background: themeColor }}>
                <DownloadSimple size={14} weight="bold" /> Download
              </button>
            </div>
          </div>
          <div className="flex-1 flex items-center justify-center px-2" onClick={e => e.stopPropagation()}>
            <img src={`${API}${lightbox.url}`} alt="" className="max-h-full max-w-full object-contain" />
          </div>
        </div>
      )}
    </div>
  );
}

function FaceRefUploader({ slug, token, themeColor, onDone }) {
  const inputRef = React.useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Since face-ref endpoint needs rsvp_id, we need to expose it from my-photos endpoint.
  // The endpoint already gives detected list scoped by token. We'll use a token-aware proxy.

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(
        `${API}/api/events/public/${slug}/my-photos/${token}/face-ref`,
        fd, { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setSuccess(true);
      setTimeout(() => { onDone(); }, 800);
    } catch (e) {
      setError(e.response?.data?.detail || 'Upload failed. Try a clear front-facing photo.');
    }
    setUploading(false);
  };

  if (success) return (
    <div className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold"
      style={{ background: `${themeColor}20`, color: themeColor }}>
      <Check size={14} weight="bold" /> Got it! Rescanning photos...
    </div>
  );

  return (
    <div className="mt-5 flex flex-col items-center gap-2">
      <button onClick={() => inputRef.current?.click()} disabled={uploading} data-testid="upload-face-ref"
        className="px-5 py-3 rounded-xl text-sm font-bold inline-flex items-center gap-2 transition-all hover:scale-105 active:scale-95 disabled:opacity-60"
        style={{ background: themeColor, color: '#fff', boxShadow: `0 8px 24px ${themeColor}40` }}>
        {uploading ? (
          <><span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" /> Looking for your face...</>
        ) : (
          <><ImageIcon size={16} weight="bold" /> Upload a selfie</>
        )}
      </button>
      {error && <p className="text-xs text-red-300 max-w-xs" data-testid="face-ref-error">{error}</p>}
      <input ref={inputRef} type="file" accept="image/*" capture="user" className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])} />
    </div>
  );
}
